import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EnvelopeCheckedIcon from "../../shared/assets/icons/EnvelopeCheckedIcon";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";

/* ── design tokens ─────────────────────────────────────────────── */
const dt = {
  // Cards & containers
  cardBg: "#FFFFFF",
  cardBorder: "#E5E7EB",
  cardShadow: "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",

  // Text hierarchy
  textPrimary: "#1a1a2e",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",

  // Accent (blue family)
  blue: "var(--accent, #3498DB)",
  blueHover: "var(--accent-hover, #2980B9)",
  blueGlow: "var(--accent-glow, rgba(52, 152, 219, 0.12))",
  blueSoft: "var(--accent-soft, rgba(52, 152, 219, 0.06))",

  // Success accent
  successGlow: "rgba(114, 196, 114, 0.15)",
  successRing: "rgba(114, 196, 114, 0.25)",

  // Radii
  radius: "14px",
  radiusSm: "8px",
  radiusLg: "20px",
  radiusPill: "24px",

  // Typography
  font: 'var(--font-sans)',
  fontMono: 'var(--font-mono)',
};

/* ── keyframes ─────────────────────────────────────────────────── */
const STYLE_ID = "email-confirmation-keyframes";
const KEYFRAMES = `
  @keyframes emailDialogFadeIn {
    0% { opacity: 0; transform: scale(0.92) translateY(16px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes emailIconPulse {
    0%, 100% { box-shadow: 0 0 0 8px rgba(114,196,114,0.08), 0 0 0 16px rgba(114,196,114,0.04); }
    50% { box-shadow: 0 0 0 12px rgba(114,196,114,0.14), 0 0 0 24px rgba(114,196,114,0.06); }
  }
  @keyframes emailContentReveal {
    0% { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes emailButtonReveal {
    0% { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
  }
`;

/* ── component ─────────────────────────────────────────────────── */
interface EmailSentConfirmationProps {
  title: string;
  description: React.ReactElement;
  open: boolean;
  buttonText?: string;
  hasLogo?: boolean;
  onClose: () => void;
}

const EmailSentConfirmation = ({
  open,
  onClose,
  title,
  description,
  hasLogo = false,
  buttonText = "Okay",
}: EmailSentConfirmationProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* inject keyframes */
  useEffect(() => {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      hideBackdrop={isMobile}
      PaperProps={{
        sx: {
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          backgroundColor: dt.cardBg,
          overflow: "hidden",
          ...(isMobile
            ? {
                margin: 0,
                borderRadius: 0,
                width: "100vw",
                height: "100vh",
                maxHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 0,
              }
            : {
                maxWidth: "560px",
                width: "560px",
                borderRadius: dt.radiusLg,
                boxShadow: dt.cardShadow,
                padding: 0,
                animation:
                  "emailDialogFadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
              }),
        },
      }}
    >
      {/* ── close button ─────────────────────────────────── */}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          zIndex: 2,
          right: isMobile ? 16 : 16,
          top: isMobile ? 16 : 16,
          color: dt.textMuted,
          width: 36,
          height: 36,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: dt.blueGlow,
            color: dt.blue,
          },
          "& .MuiSvgIcon-root": {
            fontSize: isMobile ? "22px" : "20px",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* ── main content wrapper ─────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          flex: 1,
          ...(isMobile
            ? {
                justifyContent: "space-between",
                px: "24px",
                pt: "72px",
                pb: "32px",
              }
            : {
                px: "48px",
                pt: "48px",
                pb: "40px",
              }),
        }}
      >
        {/* ── top section (logo + icon + title) ────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            ...(isMobile && { flex: "0 0 auto" }),
          }}
        >
          {/* MachineID logo */}
          {hasLogo && (
            <Box
              sx={{
                mb: 3,
                animation:
                  "emailContentReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
              }}
            >
              <MachineLogoIcon />
            </Box>
          )}

          {/* envelope icon with glow circle */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: isMobile ? 96 : 108,
              height: isMobile ? 96 : 108,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${dt.successGlow} 0%, ${dt.blueSoft} 100%)`,
              border: `2px solid ${dt.successRing}`,
              mb: 3,
              animation:
                "emailIconPulse 3s ease-in-out infinite, emailContentReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
            }}
          >
            <EnvelopeCheckedIcon
              sx={{
                width: isMobile ? 56 : 64,
                height: isMobile ? 56 : 64,
              }}
            />
          </Box>

          {/* title */}
          <Box
            sx={{
              fontSize: isMobile ? "24px" : "28px",
              fontWeight: 700,
              lineHeight: isMobile ? "30px" : "36px",
              letterSpacing: "-0.5px",
              color: dt.textPrimary,
              mb: 2.5,
              animation:
                "emailContentReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.25s both",
            }}
          >
            {title}
          </Box>
        </Box>

        {/* ── middle section (description) ─────────────── */}
        <Box
          sx={{
            width: "100%",
            ...(isMobile && {
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }),
          }}
        >
          <Box
            sx={{
              fontSize: isMobile ? "15px" : "16px",
              fontWeight: 400,
              lineHeight: "26px",
              letterSpacing: "0.1px",
              color: dt.textSecondary,
              textAlign: "center",
              px: isMobile ? 0 : 1,
              animation:
                "emailContentReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.35s both",
              /* Style the bold email span inline from callers */
              "& span": {
                fontFamily: dt.fontMono,
                fontWeight: 600,
                color: dt.textPrimary,
                backgroundColor: dt.blueSoft,
                padding: "2px 8px",
                borderRadius: "6px",
                fontSize: isMobile ? "13px" : "14px",
                letterSpacing: "0.2px",
                display: "inline-block",
                whiteSpace: "nowrap",
              },
            }}
          >
            {description}
          </Box>
        </Box>

        {/* ── bottom section (button) ──────────────────── */}
        <Box
          sx={{
            width: "100%",
            mt: isMobile ? "auto" : 4,
            pt: isMobile ? 3 : 0,
            animation:
              "emailButtonReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both",
            ...(isMobile && { flex: "0 0 auto" }),
          }}
        >
          <Button
            fullWidth
            onClick={onClose}
            variant="contained"
            disableElevation
            sx={{
              background: `linear-gradient(135deg, ${dt.blue} 0%, var(--accent-gradient-dark, #2E86C1) 100%)`,
              borderRadius: dt.radiusPill,
              padding: isMobile ? "14px 32px" : "13px 32px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "15px" : "16px",
              letterSpacing: "0.2px",
              color: "#FFFFFF",
              boxShadow: "0 4px 14px rgba(52,152,219,0.25)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: `linear-gradient(135deg, #2E86C1 0%, ${dt.blue} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(52,152,219,0.35)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 2px 8px rgba(52,152,219,0.2)",
              },
            }}
          >
            {buttonText}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EmailSentConfirmation;
